import React, { Component, Suspense, createRef, useEffect, useMemo } from "react";
import { resolveFileUrl } from "./../ricos-media/index.js";
import { VERTICAL_EMBED_TYPE, Version } from "./../ricos-content/index.js";
import { resolveTheme } from "./../ricos-styles/resolve-theme.js";
import { ThemeProvider } from "./../ricos-styles/theme-provider.js";
import { useBiHelpers } from "./../_shared/bi-strategy/use-bi-helpers.js";
import { deepMerge } from "./../_shared/deep-merge.js";
import { extractPluginsConfig } from "./../_shared/extract-plugins-config.js";
import { hasPlugin } from "./../_shared/has-plugin.js";
import { parseInstance } from "./../_shared/request-service-provider/parse-instance.js";
import { RequestServiceProvider } from "./../_shared/request-service-provider/request-service-provider.js";
import { WixInstanceContextProvider } from "./../_shared/request-service-provider/wix-instance-context.js";
import { getImageUrl } from "./../_shared/url-resolvers.js";
import { useIframeSandboxDomain } from "./../_shared/use-iframe-sandbox-domain.js";
import { addDevToolsClickListener } from "./../wix-rich-content-common/index.js";
import {
  RicosDevToolsLazy,
  shouldShowDebuggingInfo
} from "./../wix-rich-content-common/lib/dev-tools.js";
import { getContentSummary } from "./../wix-rich-content-common/lib/ricos-content-analytics.js";
import { changeBaseUrl } from "./change-base-url.js";
import { ContentViewer } from "./ContentViewer/index.js";
import { isViewerPlugin } from "./is-viewer-plugin.js";
import { mergeViewerProps } from "./utils.js";
class RicosViewerNext extends Component {
  constructor(props) {
    super(props);
    this.loadTextSelection = () => {
      const { textSelectionToolbar, isMobile } = this.props;
      const needTextSelectionToolbar = textSelectionToolbar && !isMobile;
      if (needTextSelectionToolbar && !this.state.TextSelectionToolbar) {
        const TextSelectionToolbar = React.lazy(() => import("./text-selection-toolbar.js"));
        this.setState({ TextSelectionToolbar });
      }
    };
    this.onMouseOver = () => {
      this.loadTextSelection();
    };
    this.getBiCallback = (key) => {
      const { children, helpers } = this.props;
      return children?.props.helpers?.[key] || helpers?.[key];
    };
    this.viewerRef = createRef();
    this.state = {};
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, errorInfo) {
    console.error({ error, errorInfo });
  }
  componentDidMount() {
    addDevToolsClickListener(
      this.viewerRef.current,
      () => this.setState({ debugInvokedWithMouse: true })
    );
  }
  render() {
    const { seoSettings, mediaSettings, content, debugMode, isPrint } = this.props;
    const { TextSelectionToolbar } = this.state;
    const mergedProps = mergeViewerProps({
      ...this.props,
      viewerProps: {
        seoMode: seoSettings,
        onMouseOver: this.onMouseOver,
        locale: this.props.locale,
        isPrint
      }
    });
    try {
      if (this.state.error) {
        this.props.onError?.(this.state.error);
        return null;
      }
      return [
        /* @__PURE__ */ React.createElement(
          ContentViewer,
          {
            ...mergedProps,
            content: this.state.content ?? mergedProps.content,
            config: mergedProps.config,
            onHover: () => {
            },
            key: "viewer",
            setRef: this.viewerRef,
            fullscreenProps: mediaSettings?.fullscreenProps,
            devTools: this.state.debugInvokedWithMouse || shouldShowDebuggingInfo(debugMode?.includes("all")) ? /* @__PURE__ */ React.createElement(React.Suspense, null, /* @__PURE__ */ React.createElement(
              RicosDevToolsLazy,
              {
                schema: {
                  getContent: () => this.state.content ?? mergedProps.content,
                  setContent: (content2) => this.setState({ content: content2 })
                },
                version: Version.currentVersion,
                mode: "viewer",
                contentState: content,
                ricosProps: this.props,
                autoOpen: this.state.debugInvokedWithMouse ?? false
              }
            )) : null
          }
        ),
        TextSelectionToolbar ? /* @__PURE__ */ React.createElement(Suspense, { key: "TextSelectionToolbar", fallback: /* @__PURE__ */ React.createElement("div", null) }, /* @__PURE__ */ React.createElement(
          TextSelectionToolbar,
          {
            onButtonClick: this.getBiCallback("onViewerAction"),
            container: this.viewerRef.current
          }
        )) : null
      ];
    } catch (e) {
      this.props.onError?.(e);
      return null;
    }
  }
}
RicosViewerNext.displayName = "RicosViewerNext";
RicosViewerNext.defaultProps = {
  onError: (err) => {
    throw err;
  }
};
const RicosViewerWithHelpers = (props) => {
  const {
    biSettings,
    instance,
    wixClient,
    iframeSandboxDomain: explicitDomain,
    theme,
    baseUrl,
    ...restProps
  } = props;
  const { helpers: biHelpers, biLoadPromise } = useBiHelpers({ instance, biSettings });
  const { metaSiteId } = useMemo(() => parseInstance(props.instance), [props.instance]);
  useEffect(() => {
    biLoadPromise.then((callbacks) => {
      const content = props.content;
      const { pluginsCount } = content && getContentSummary(content) || {};
      if (pluginsCount) {
        callbacks.onViewerLoaded?.({
          isPreview: false,
          version: Version.currentVersion,
          pluginsCount,
          url: document.URL,
          contentId: content?.metadata?.id
        });
      }
    });
  }, []);
  const iframeSandboxDomain = useIframeSandboxDomain(explicitDomain, instance);
  const resolvedTheme = useMemo(
    () => resolveTheme({ theme, isMobile: restProps.isMobile }),
    [theme, restProps.isMobile]
  );
  const viewerPlugins = useMemo(() => props.plugins?.filter(isViewerPlugin), [props.plugins]);
  const extractedPluginConfig = useMemo(() => extractPluginsConfig(viewerPlugins), [viewerPlugins]);
  const verticalEmbedConfig = useMemo(() => {
    if (baseUrl && hasPlugin(VERTICAL_EMBED_TYPE, viewerPlugins)) {
      return {
        [VERTICAL_EMBED_TYPE]: {
          changeBaseUrl: (url) => changeBaseUrl(url, baseUrl)
        }
      };
    }
    return {};
  }, [baseUrl, viewerPlugins]);
  const pluginConfig = useMemo(
    () => deepMerge({}, extractedPluginConfig.config, verticalEmbedConfig),
    [extractedPluginConfig, verticalEmbedConfig]
  );
  const helpers = useMemo(
    () => ({
      ...biHelpers,
      getImageUrl,
      resolveFileUrl: (
        // Some urls might be resolved even without msid or instance, so we call it regardless
        resolveFileUrl(props.msid ?? metaSiteId ?? "", props.instance ?? "")
      )
    }),
    [biHelpers, props.msid, props.instance, metaSiteId]
  );
  return /* @__PURE__ */ React.createElement(RequestServiceProvider, { wixClient, instance }, /* @__PURE__ */ React.createElement(WixInstanceContextProvider, { instance }, /* @__PURE__ */ React.createElement(ThemeProvider, { theme: resolvedTheme }, /* @__PURE__ */ React.createElement(
    RicosViewerNext,
    {
      ...restProps,
      msid: props.msid ?? metaSiteId,
      pluginConfig,
      helpers,
      iframeSandboxDomain,
      theme: resolvedTheme,
      baseUrl
    }
  ))));
};
RicosViewerWithHelpers.displayName = "RicosViewer";
export {
  RicosViewerWithHelpers
};
//# sourceMappingURL=ricos-viewer-next.js.map
